/**
 * Offers back a note recording that never finished saving.
 *
 * Mounted once in the authenticated app shell, so it runs on ANY page, not only
 * the note: the person who closed their laptop mid-upload comes back to the
 * dashboard. It lists the signed-in user's takes on this device that no tab is
 * recording or uploading (`listRecoverableRecordings`), one at a time, and
 * uploads the chosen one to the note it was recorded on. The take is removed
 * from the device only after finalize succeeds; a discard asks first.
 *
 * Re-scans when a recorder in this tab lets go of a take (`onRecordingReleased`),
 * which is how a take whose upload failed after leaving its note shows up here
 * without a reload.
 */

import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildArtifactParentType } from '@bower/shared/constants/parent-validity';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { AudioPlayer } from '../../ui/AudioPlayer';
import { apiService } from '../../../services/api.service';
import { getArtifact } from '../../../services/api/artifacts.api';
import { useAuth } from '../../../contexts/AuthContext';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import { useRecordingActivityGuard } from '../../../stores/recordingActivityStore';
import { uploadRecordedAudio } from '../../../services/upload/uploadRecordedAudio';
import {
  type DurableRecordingMeta,
  clearFinalizedRecording,
  deleteRecording,
  holdRecording,
  listRecoverableRecordings,
  onRecordingReleased,
  readRecordingBlob,
  recordingExists,
  updateMeta,
} from '../../../services/recording/durableRecording';
import { voiceFileName } from '../../../utils/audioFormat';
import { formatDate, generateVoiceNoteTitle } from '../../../utils/timeUtils';
import { safeAnalytics } from '../../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { reportRecoveryOffered } from '../../../utils/recordingRecoveryTelemetry';

type Phase = 'offer' | 'confirm-discard' | 'uploading' | 'failed';

const TITLE: Record<Phase, string> = {
  offer: 'A recording wasn’t saved',
  failed: 'A recording wasn’t saved',
  uploading: 'Saving your recording',
  'confirm-discard': 'Discard this recording?',
};

function telemetry(meta: DurableRecordingMeta, sizeBytes?: number) {
  return {
    workspace_id: meta.workspaceId,
    duration_ms: meta.durationMs,
    was_complete: meta.status === 'stopped' && !meta.partial,
    age_ms: Date.now() - meta.updatedAt,
    ...(sizeBytes !== undefined && { size_bytes: sizeBytes }),
  };
}

/** "47 min" or "12 sec": how long the take on the device is. */
function lengthLabel(durationMs: number): string {
  const seconds = Math.round(durationMs / 1000);
  return seconds >= 60 ? `${Math.round(seconds / 60)} min` : `${seconds} sec`;
}

/**
 * The title of the note a take belongs to, so the dialog can say which one.
 * Asked of the take's OWN workspace (it may not be the one open now). A note
 * that is gone or no longer visible resolves to null rather than an error: the
 * dialog still has to offer the discard.
 */
function useRecoveryNoteTitle(meta: DurableRecordingMeta) {
  return useQuery({
    queryKey: ['recording-recovery-note', meta.workspaceId, meta.artifactId],
    queryFn: async (): Promise<string | null> => {
      const response = await getArtifact(apiService, meta.artifactId, meta.workspaceId);
      if (!response.success) return null;
      const title = (response.data as { title?: unknown } | undefined)?.title;
      return typeof title === 'string' && title.trim() ? title : 'Untitled note';
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * A playable URL for the take still on this device. Keyed on the take's id and
 * chunk count, not the object: every re-scan (any recorder in this tab letting go of a
 * take) reads fresh copies from IndexedDB, and keying on identity restarted the
 * player mid-listen.
 */
function useRecordingPreviewUrl(meta: DurableRecordingMeta): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const metaRef = useRef(meta);
  metaRef.current = meta;
  const { id, chunkCount } = meta;
  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    void readRecordingBlob(metaRef.current)
      .then((blob) => {
        if (revoked || !blob || blob.size === 0) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [id, chunkCount]);
  return url;
}

/** Which note, when, how long, and a player: enough to tell this take apart. */
function RecordingDetails({ meta }: { meta: DurableRecordingMeta }) {
  const note = useRecoveryNoteTitle(meta);
  const previewUrl = useRecordingPreviewUrl(meta);
  const recordedAt = formatDate(new Date(meta.startedAt), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  let noteLabel: ReactNode;
  if (note.isPending) noteLabel = <Skeleton height="1em" width="12em" />;
  else if (note.data) noteLabel = note.data;
  else noteLabel = 'A note you can no longer open';
  return (
    <VStack
      align="stretch"
      spacing={2}
      p={3}
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      data-testid="recovery-recording-details"
    >
      <HStack spacing={2} align="center">
        <MaterialIcon name="description" boxSize={4} color="text.secondary" />
        <Text fontSize="sm" fontWeight="semibold" color="text.primary" noOfLines={2}>
          {noteLabel}
        </Text>
      </HStack>
      <HStack spacing={2} align="center">
        <MaterialIcon name="schedule" boxSize={4} color="text.secondary" />
        <Text fontSize="sm" color="text.secondary">
          Recorded {recordedAt} · {lengthLabel(meta.durationMs)}
        </Text>
      </HStack>
      {previewUrl && (
        <Box pt={1}>
          <AudioPlayer src={previewUrl} surface="recording_recovery" />
        </Box>
      )}
    </VStack>
  );
}

function retryHandleOf(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('retryFinalizeUploadId' in error)) {
    return undefined;
  }
  const handle = (error as { retryFinalizeUploadId?: unknown }).retryFinalizeUploadId;
  return typeof handle === 'string' ? handle : undefined;
}

export function RecordingRecoveryPrompt() {
  const { user } = useAuth();
  const toast = useAppToast();
  const ownerIds = useMemo(
    () => [user?.internalId, user?.uid].filter((id): id is string => Boolean(id)),
    [user?.internalId, user?.uid],
  );
  const [queue, setQueue] = useState<DurableRecordingMeta[]>([]);
  const [phase, setPhase] = useState<Phase>('offer');
  const [progress, setProgress] = useState(0);
  // Closing (the X) holds for the rest of this page load; the next load asks again.
  const dismissed = useRef(new Set<string>());
  const offered = useRef(new Set<string>());
  const current = queue[0] ?? null;
  // A scan that lands mid-upload or mid-confirm must not swap the take the
  // dialog is acting on: keep the head while any phase but 'offer' is open.
  const phaseRef = useRef<Phase>('offer');
  phaseRef.current = phase;

  useRecordingActivityGuard(phase === 'uploading');

  const scan = useCallback(async () => {
    if (ownerIds.length === 0) {
      setQueue([]);
      return;
    }
    let found: DurableRecordingMeta[];
    try {
      found = await listRecoverableRecordings(ownerIds);
    } catch {
      // No IndexedDB (private mode, blocked storage): nothing was saved to offer.
      found = [];
    }
    setQueue((q) => {
      const head = phaseRef.current === 'offer' ? undefined : q[0];
      const rest = found.filter((m) => !dismissed.current.has(m.id) && m.id !== head?.id);
      return head ? [head, ...rest] : rest;
    });
  }, [ownerIds]);

  useEffect(() => {
    void scan();
    return onRecordingReleased(() => void scan());
  }, [scan]);

  useEffect(() => {
    if (!current || offered.current.has(current.id)) return;
    offered.current.add(current.id);
    // Usually before analytics init: stashed and flushed after (see the module).
    const { workspace_id, duration_ms, was_complete, age_ms } = telemetry(current);
    reportRecoveryOffered({ workspace_id, duration_ms, was_complete, age_ms });
  }, [current]);

  const next = useCallback(() => {
    setPhase('offer');
    setProgress(0);
    setQueue((q) => q.slice(1));
  }, []);

  const upload = useCallback(async () => {
    if (!current) return;
    setPhase('uploading');
    setProgress(0);
    const release = await holdRecording(current.id, { ifAvailable: true });
    if (!release) {
      // Another tab picked it up between the scan and this click.
      next();
      return;
    }
    let sizeBytes: number | undefined;
    try {
      // Another tab may have saved or discarded it since the scan: then there
      // is nothing to do, and no failure to report.
      if (!(await recordingExists(current.id))) {
        release();
        next();
        return;
      }
      const blob = await readRecordingBlob(current);
      if (!blob || blob.size === 0) throw new Error('The saved recording could not be read.');
      sizeBytes = blob.size;
      const title = generateVoiceNoteTitle(new Date(current.startedAt));
      const file = new File([blob], voiceFileName(title, current.mimeType), {
        type: current.mimeType,
      });
      await uploadRecordedAudio(
        file,
        {
          workspaceId: current.workspaceId,
          parentEntityType: buildArtifactParentType('note'),
          parentEntityId: current.artifactId,
          title,
          importAsNotes: false,
          multipleSpeakers: current.multipleSpeakers,
          durationMs: current.durationMs,
          serverTranscription: true,
          ...(current.resumeFinalizeUploadId && {
            resumeFinalizeUploadId: current.resumeFinalizeUploadId,
          }),
        },
        setProgress,
      );
      // Durable on the server: only now does the device copy go. A failed
      // delete marks it finalized, so it is never offered back as unsaved.
      if (!(await clearFinalizedRecording(current.id))) {
        safeAnalytics.track(ANALYTICS_EVENTS.VOICE_RECORDING_BACKUP_FAILED, {
          workspace_id: current.workspaceId,
          stage: 'clear',
        });
      }
      safeAnalytics.track(
        ANALYTICS_EVENTS.VOICE_RECORDING_RECOVERY_RESTORED,
        telemetry(current, sizeBytes),
      );
      toast({
        title: 'Recording saved',
        description: 'It is on its note now, and the transcript will appear there.',
        status: 'success',
      });
      release();
      next();
    } catch (error) {
      const handle = retryHandleOf(error);
      await updateMeta(current.id, { resumeFinalizeUploadId: handle }).catch(() => undefined);
      setQueue((q) =>
        q.map((m) => (m.id === current.id ? { ...m, resumeFinalizeUploadId: handle } : m)),
      );
      safeAnalytics.track(ANALYTICS_EVENTS.VOICE_RECORDING_RECOVERY_RESTORE_FAILED, {
        ...telemetry(current, sizeBytes),
        error_name: error instanceof Error ? error.name : 'Unknown',
      });
      setPhase('failed');
      release();
    }
  }, [current, next, toast]);

  const discard = useCallback(async () => {
    if (!current) return;
    // Same claim as an upload: never delete a take another tab is saving.
    const release = await holdRecording(current.id, { ifAvailable: true });
    if (!release) {
      next();
      return;
    }
    try {
      await deleteRecording(current.id);
    } catch {
      toast({
        title: "Couldn't discard the recording",
        description: 'It is still on this device. Try again.',
        status: 'error',
      });
      return;
    } finally {
      release();
    }
    safeAnalytics.track(ANALYTICS_EVENTS.VOICE_RECORDING_RECOVERY_DISCARDED, telemetry(current));
    next();
  }, [current, next, toast]);

  const notNow = useCallback(() => {
    if (current) dismissed.current.add(current.id);
    next();
  }, [current, next]);

  if (!current) return null;

  const uploading = phase === 'uploading';
  const offerCopy = [
    phase === 'failed' ? 'The upload didn’t finish.' : '',
    'This recording is still on this device but never reached its note.',
    current.status === 'recording' ? 'The page closed while it was still recording.' : '',
    current.partial ? 'Only the first part of it was saved on this device.' : '',
  ]
    .filter(Boolean)
    .join(' ');

  let body: ReactNode;
  if (phase === 'confirm-discard') {
    body = (
      <Text fontSize="md" color="text.secondary" lineHeight="tall">
        It is only on this device. Once discarded it can’t be recovered.
      </Text>
    );
  } else if (uploading) {
    body = (
      <VStack align="stretch" spacing={3}>
        <Text fontSize="md" color="text.secondary" lineHeight="tall">
          Keep this tab open until it finishes.
        </Text>
        <Progress
          value={progress}
          size="sm"
          borderRadius="full"
          aria-label="Recording upload progress"
        />
      </VStack>
    );
  } else {
    body = (
      <VStack align="stretch" spacing={3}>
        <Text fontSize="md" color="text.secondary" lineHeight="tall">
          {offerCopy}
        </Text>
        <RecordingDetails meta={current} />
      </VStack>
    );
  }

  return (
    <Modal
      isOpen
      onClose={uploading ? () => undefined : notNow}
      isCentered
      size={{ base: 'full', md: 'md' }}
      closeOnOverlayClick={false}
      closeOnEsc={!uploading}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent mx={{ base: 0, md: 4 }}>
        <ModalHeader pb={2}>
          <VStack spacing={3} align="flex-start">
            <MaterialIcon name="mic" boxSize={8} color="text.warning" />
            <Text fontSize="lg" fontWeight="semibold" color="text.primary">
              {TITLE[phase]}
            </Text>
          </VStack>
        </ModalHeader>
        {/* Closing asks again on the next load ("not now"); never mid-upload. */}
        {!uploading && <ModalCloseButton aria-label="Close, ask me again later" />}

        <ModalBody py={4}>{body}</ModalBody>

        <ModalFooter gap={3} pt={2}>
          {phase === 'confirm-discard' ? (
            <>
              <Button variant="outline" onClick={() => setPhase('offer')}>
                Keep it
              </Button>
              <Button
                variant="danger"
                leftIcon={<MaterialIcon name="delete" boxSize="18px" />}
                onClick={() => void discard()}
              >
                Discard
              </Button>
            </>
          ) : (
            <>
              {/* The theme's bordered destructive action beside a primary CTA. */}
              <Button
                variant="outline-danger"
                leftIcon={<MaterialIcon name="delete" boxSize="18px" />}
                onClick={() => setPhase('confirm-discard')}
                isDisabled={uploading}
              >
                Discard
              </Button>
              <Button onClick={() => void upload()} isLoading={uploading} loadingText="Uploading">
                {phase === 'failed' ? 'Try again' : 'Upload to note'}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
